// src/components/quiz/QuestionMC.jsx
import React from 'react';
import { CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import TranslatableText from '../TranslatableText';

export default function QuestionMC({ 
  question, 
  options, 
  selected, 
  feedback, 
  onAnswer, 
  tempSelected, 
  setTempSelected 
}) {
  
  // ✅ LÓGICA SMART GRID: Si hay opciones largas, usamos 1 columna
  const isLongList = options.some(opt => (opt?.text || opt).length > 15);

  // Calcular número de blanks para mostrar progreso
  const totalBlanks = (question.q.match(/___/g) || []).length;
  const hasBlanks = question.q.includes('___');

  const renderQuestion = (text) => {
    if (!text.includes('___')) {
      return <h3 className="text-lg md:text-2xl font-black text-slate-800 mb-2 md:mb-4"><TranslatableText text={text} /></h3>;
    }

    const parts = text.split('___');
    
    // Crear array de las partes seleccionadas
    const selectedParts = (selected || tempSelected) 
      ? (selected || tempSelected).split('/').map(p => p.trim()) 
      : [];

    return (
      <h3 className="text-lg md:text-2xl font-black text-slate-800 leading-relaxed">
        {parts.map((part, i) => (
          <span key={i}>
            <TranslatableText text={part} />
            {i < parts.length - 1 && (
              <span
                className={`inline-block border-b-4 mx-1 px-2 min-w-[60px] md:min-w-[80px] text-center transition-all duration-300 font-bold text-sm md:text-base rounded-t-lg
                  ${(selected || tempSelected)
                    ? (feedback 
                        ? (feedback === 'correct' 
                            ? 'border-green-500 text-green-600 bg-green-50 scale-105' 
                            : 'border-red-500 text-red-600 bg-red-50 animate-shake')
                        : 'border-indigo-500 text-indigo-600 bg-indigo-50')
                    : 'border-slate-300 text-slate-400 bg-slate-50'
                  }`}
              >
                {selectedParts[i] || "..."}
              </span>
            )}
          </span>
        ))}
      </h3>
    );
  };

  return (
    <div className="animate-in fade-in duration-500">
      {/* TARJETA DE PREGUNTA CON FEEDBACK VISUAL */}
      <div className={`bg-white p-4 md:p-8 rounded-[2rem] md:rounded-[2.5rem] shadow-sm border-2 transition-all duration-300 min-h-[100px] md:min-h-[180px] flex flex-col items-center justify-center text-center relative mb-3 md:mb-4 ${
        feedback === 'correct' 
          ? 'border-green-300 bg-green-50/30 shadow-green-100' 
          : feedback === 'wrong' 
            ? 'border-red-300 bg-red-50/30 shadow-red-100' 
            : 'border-slate-100'
      }`}>
        
        {/* Etiqueta superior */}
        <span className="absolute top-2 md:top-4 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-300">
          ELIGE LA CORRECTA
        </span>

        {/* Icono de feedback */}
        {feedback === 'correct' && (
          <CheckCircle2 
            className="absolute top-2 right-2 md:top-4 md:right-4 text-green-500 animate-in zoom-in" 
            size={24} 
          />
        )}
        {feedback === 'wrong' && (
          <XCircle 
            className="absolute top-2 right-2 md:top-4 md:right-4 text-red-500 animate-in zoom-in" 
            size={24} 
          />
        )}

        {renderQuestion(question.q)}

        {/* Contador de progreso para preguntas con blanks */}
        {hasBlanks && totalBlanks > 1 && (
          <div className="mt-3 flex items-center justify-center gap-2">
            {[...Array(totalBlanks)].map((_, i) => (
              <div
                key={i}
                className={`w-6 h-1.5 rounded-full transition-all duration-300 ${
                  (selected || tempSelected) && i === 0
                    ? 'bg-indigo-500 scale-110' 
                    : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* MENSAJE DE AYUDA CONTEXTUAL */}
      {!feedback && !tempSelected && (
        <div className="text-center mb-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <p className="text-xs font-bold text-slate-400 flex items-center justify-center gap-1">
            <Sparkles size={12} className="text-indigo-400" />
            Selecciona una opción
            <Sparkles size={12} className="text-indigo-400" />
          </p>
        </div>
      )}

      {/* GRID DINÁMICO DE OPCIONES */}
      <div className={`grid ${isLongList ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-2 md:gap-3 mb-4 md:mb-6`}>
        {options.map((opt, idx) => {
          const optValue = opt?.text || opt;
          const optKey = opt?.id || opt;
          const isSelected = (selected === optValue || tempSelected === optValue);
          const isDisabled = !!feedback;

          return (
            <button
              key={optKey}
              disabled={isDisabled}
              onClick={() => !feedback && setTempSelected(optValue)}
              className={`group relative p-3 md:p-5 rounded-xl md:rounded-2xl font-bold text-sm md:text-lg border-b-4 transition-all text-center flex items-center justify-center min-h-[50px] md:min-h-[70px] overflow-hidden
                ${isSelected
                  ? (feedback 
                      ? (feedback === 'correct' 
                          ? 'bg-green-500 border-green-700 text-white scale-105 shadow-xl shadow-green-100' 
                          : 'bg-red-500 border-red-700 text-white scale-105 shadow-xl shadow-red-100')
                      : 'bg-indigo-600 border-indigo-900 text-white shadow-xl shadow-indigo-200 scale-105 ring-4 ring-indigo-200')
                  : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300 hover:text-indigo-600 hover:scale-102 hover:-translate-y-1 active:scale-95 active:translate-y-0 shadow-sm'
                }
                ${isDisabled && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              style={{ 
                animationDelay: `${idx * 50}ms`,
                transitionProperty: 'all'
              }}
            >
              {/* Efecto shimmer en opción seleccionada */}
              {isSelected && !feedback && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
              )}

              {/* Contenido */}
              <span className="relative z-10">
  {optValue} 
</span>

              {/* Checkmark en opción correcta */}
              {isSelected && feedback === 'correct' && (
                <CheckCircle2 
                  className="absolute top-2 right-2 animate-in zoom-in" 
                  size={16} 
                />
              )}

              {/* X en opción incorrecta */}
              {isSelected && feedback === 'wrong' && (
                <XCircle 
                  className="absolute top-2 right-2 animate-in zoom-in" 
                  size={16} 
                />
              )}
            </button>
          );
        })}
      </div>

      {/* MENSAJE DE CONFIRMACIÓN */}
      {tempSelected && !feedback && (
        <div className="text-center mb-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <p className="text-xs font-bold text-indigo-600 bg-indigo-50 py-2 px-4 rounded-xl inline-block">
            ¿Confirmar respuesta? 💡
          </p>
        </div>
      )}

      {/* BOTÓN COMPROBAR MEJORADO */}
      {tempSelected && !feedback && (
        <button 
          onClick={() => onAnswer(tempSelected)}
          className="w-full bg-indigo-600 text-white py-3 md:py-4 rounded-2xl font-black text-lg md:text-xl border-b-8 border-indigo-900 active:border-b-0 active:translate-y-2 shadow-xl shadow-indigo-100 hover:bg-indigo-500 transition-all animate-in slide-in-from-bottom-4 duration-300 flex items-center justify-center gap-2"
        >
          COMPROBAR
          <CheckCircle2 size={20} />
        </button>
      )}

      {/* ESTILOS ADICIONALES */}
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); }
          20%, 40%, 60%, 80% { transform: translateX(3px); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shake {
          animation: shake 0.4s ease-in-out;
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .hover\:scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
}
